/**
 * useMaintActionSubmit — Wave 10 RAM/RCM A5 BLOCKER.
 *
 * Shared hook used by every RAM/RCM action dialog to fan out to its
 * `/v3/maint/<action-type>` endpoint. The hook surfaces a single
 * { submit, submitting, error } API so dialogs can swap their internal
 * direct-jxchange call for a centrally-routed, auditable, approval-aware
 * submit without re-implementing the loading/error UX.
 *
 * The hook never knows the action type — each dialog imports the typed
 * client method (e.g. client.retail.maintActions.submitChangeOdLimit) and
 * passes it as the `submitFn`. That keeps the per-call typing correct
 * while sharing the error/lifecycle plumbing.
 *
 * Usage:
 *   const { submit, submitting, error } = useMaintActionSubmit();
 *   const result = await submit(client.retail.maintActions.submitChangeOdLimit, {
 *     accountNumber, newLimitAmount, ...
 *   });
 *   if (result.success && result.data?.approvalRequired) {...}
 */

import { useCallback, useState } from 'react';
import type {
  ApiResult,
  MaintActionSubmitResponse,
} from '@retail-ops/shared-types';

export interface MaintSubmitOutcome {
  success: boolean;
  data?: MaintActionSubmitResponse;
  error?: string;
}

export function useMaintActionSubmit(): {
  submit: <TBody>(
    submitFn: (body: TBody) => Promise<ApiResult<MaintActionSubmitResponse>>,
    body: TBody,
  ) => Promise<MaintSubmitOutcome>;
  submitting: boolean;
  error: string | null;
  reset: () => void;
} {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback((): void => {
    setError(null);
    setSubmitting(false);
  }, []);

  const submit = useCallback(
    async <TBody,>(
      submitFn: (body: TBody) => Promise<ApiResult<MaintActionSubmitResponse>>,
      body: TBody,
    ): Promise<MaintSubmitOutcome> => {
      setSubmitting(true);
      setError(null);
      try {
        const res = await submitFn(body);
        const env = res as unknown as {
          success?: boolean;
          data?: MaintActionSubmitResponse;
          error?: string;
        };
        if (env.success === false) {
          const msg = env.error ?? 'Submit failed.';
          setError(msg);
          return { success: false, error: msg };
        }
        return { success: true, data: env.data };
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Unknown error.';
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [],
  );

  return { submit, submitting, error, reset };
}
